import { useState, type FormEvent } from 'react';
import { Button, Checkbox, Input, Label, Spinner, makeStyles } from '@fluentui/react-components';
import { ErrorCircle20Filled, ShieldKeyhole24Regular } from '@fluentui/react-icons';
import { useSearchParams } from 'react-router-dom';

import { ApiClientError } from '../api/client.js';
import { reenviarCodigo, verificarCodigo } from '../api/auth.js';
import { codigoCompleto, leerVerificacion, normalizarCodigo } from '../lib/verificarAcceso.js';
import { brandingProductoActivo } from '../lib/brandingDelProducto.js';

/**
 * SEGUNDO PASO DE LA ENTRADA: EL CÓDIGO QUE LLEGÓ POR CORREO.
 *
 * Se llega acá desde el login (equipo nuevo o cuenta de staff), desde el botón de OPUS 27
 * o el hub (el servidor redirige) y al cambiar a una empresa de staff. Al verificar, se
 * recarga en la ruta de regreso para que la sesión nueva se lea desde cero.
 */
const useStyles = makeStyles({
  page: { minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--space-xxl)' },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-2xl)',
    padding: 'var(--space-4xl)',
    boxShadow: 'var(--shadow-28)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xl)',
  },
  header: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-m)', textAlign: 'center' },
  icon: { fontSize: '32px', color: 'var(--opus-brand-60)' },
  title: { fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-500)', margin: 0 },
  sub: { fontSize: 'var(--fs-300)', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-l)' },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' },
  code: { fontFamily: 'var(--font-mono)', letterSpacing: '0.4em', fontSize: 'var(--fs-500)' },
  alert: {
    padding: 'var(--space-m) var(--space-l)',
    borderRadius: 'var(--radius-md)',
    background: 'rgba(232, 17, 35, 0.08)',
    border: '1px solid rgba(232, 17, 35, 0.35)',
    fontSize: 'var(--fs-300)',
    display: 'flex',
    gap: 'var(--space-s)',
    alignItems: 'flex-start',
  },
  info: { fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)', textAlign: 'center' },
  footer: { fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)', textAlign: 'center' },
});

export function VerificarAccesoPage() {
  const styles = useStyles();
  const [params] = useSearchParams();
  const datos = leerVerificacion(params);
  const [codigo, setCodigo] = useState('');
  const [recordar, setRecordar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const marca = brandingProductoActivo();

  if (!datos) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.sub}>Este enlace de verificación no es válido.</p>
          <Button appearance="primary" as="a" href="/login">Volver a iniciar sesión</Button>
        </div>
      </div>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!datos || !codigoCompleto(codigo)) {
      setError('Escribe los 6 dígitos del código.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await verificarCodigo(datos.desafio, normalizarCodigo(codigo), recordar);
      const destino = 'redirect' in r && typeof r.redirect === 'string' ? r.redirect : datos.volver;
      // Recarga completa: la sesión nueva se lee desde cero (AuthBootstrap).
      window.location.assign(destino);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'No se pudo verificar el código. Intenta de nuevo.');
      setEnviando(false);
    }
  }

  async function reenviar() {
    if (!datos) return;
    setError(null);
    try {
      const r = await reenviarCodigo(datos.desafio);
      setAviso(r.message);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'No se pudo reenviar el código.');
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <ShieldKeyhole24Regular className={styles.icon} aria-hidden />
          <h1 className={styles.title}>Verifica que eres tú</h1>
          <p className={styles.sub}>
            Te mandamos un código de 6 dígitos{datos.destino ? ` a ${datos.destino}` : ' a tu correo'}.
            Escríbelo para entrar a {marca.nombre}.
          </p>
        </div>

        {error && (
          <div className={styles.alert} role="alert">
            <ErrorCircle20Filled style={{ color: '#e81123', flexShrink: 0, marginTop: 2 }} />
            <span>{error}</span>
          </div>
        )}
        {aviso && !error && <p className={styles.info} role="status">{aviso}</p>}

        <form className={styles.form} onSubmit={(e) => void onSubmit(e)}>
          <div className={styles.field}>
            <Label htmlFor="codigo" weight="semibold">Código de verificación</Label>
            <Input
              id="codigo"
              className={styles.code}
              value={codigo}
              onChange={(_, d) => setCodigo(normalizarCodigo(d.value))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              disabled={enviando}
              placeholder="000000"
            />
          </div>
          <Checkbox
            checked={recordar}
            onChange={(_, d) => setRecordar(Boolean(d.checked))}
            label="Recordar este equipo (no volver a pedir el código aquí por un tiempo)"
            disabled={enviando}
          />
          <Button
            appearance="primary"
            size="large"
            type="submit"
            disabled={enviando || !codigoCompleto(codigo)}
            icon={enviando ? <Spinner size="tiny" /> : undefined}
          >
            {enviando ? 'Verificando…' : 'Verificar y entrar'}
          </Button>
          <Button appearance="subtle" type="button" onClick={() => void reenviar()} disabled={enviando}>
            Mandarme otro código
          </Button>
          <Button appearance="transparent" as="a" href="/login">Volver a iniciar sesión</Button>
        </form>

        <p className={styles.footer}>
          Si no intentaste entrar, ignora el correo y cambia tu contraseña de OPUS.
        </p>
      </div>
    </div>
  );
}
